import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSport } from "../contexts/SportContext";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { 
  Bell, User, LogOut, Settings, Crown, Shield, HelpCircle, 
  FileText, MessageSquare, ChevronRight, X, Loader2, ShoppingBag
} from "lucide-react";
import NotificationDropdown from "./NotificationDropdown";
import { setAdBannerVisible } from "./AdBanner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TopBar() {
  const { user, dbUser, logout, getAsyncAuthHeader } = useAuth();
  const { sport } = useSport();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const profileRef = useRef(null);
  const notifRef = useRef(null);

  // Fetch unread count - wait for dbUser to ensure auth is fully ready
  useEffect(() => {
    if (!user || !dbUser) return;
    
    const fetchUnread = async () => {
      try {
        const headers = await getAsyncAuthHeader();
        const res = await axios.get(`${API}/notifications`, { headers });
        setUnreadCount(res.data.unread_count || 0);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };
    
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [user, dbUser, getAsyncAuthHeader]);

  // Hide ad banner when any dropdown is open (native layer conflict)
  useEffect(() => {
    const anyDropdownOpen = showProfile || showNotifications;
    setAdBannerVisible(!anyDropdownOpen);
  }, [showProfile, showNotifications]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfile(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowProfile(false);
    navigate("/auth");
  };

  const getInitial = () => {
    if (dbUser?.display_name) return dbUser.display_name.charAt(0).toUpperCase();
    if (user?.displayName) return user.displayName.charAt(0).toUpperCase();
    return "U";
  };

  // Build menu items - sport-aware routing (cricket vs football). Settings,
  // Feedback and Privacy are sport-agnostic. Pro is cricket-only for v1.
  const isFootball = sport === "football";
  const isFreeCricket = !isFootball && (!dbUser?.tier || dbUser.tier === "free");
  const menuItems = [
    { label: "Profile", icon: User, path: "/profile" },
    { label: isFootball ? "My Squad" : "My Team", icon: Crown, path: isFootball ? "/football/squad-builder" : "/my-team" },
    ...(isFootball ? [{ label: "Shop", icon: ShoppingBag, path: "/subscribe" }] : [{ label: "Upgrade to Pro", icon: Shield, path: "/subscribe", emphasize: isFreeCricket }]),
    { label: "Point System", icon: HelpCircle, path: isFootball ? "/football/point-system" : "/point-system" },
    { label: "Feedback", icon: MessageSquare, path: "/feedback" },
    { label: "Settings", icon: Settings, path: "/settings" },
    { label: "Privacy Policy", icon: FileText, path: "/privacy" },
    // Admin Panel - only for admins, routes to the active sport's admin entry
    ...(dbUser?.is_admin ? [{ label: "Admin Panel", icon: Settings, path: isFootball ? "/admin/football/match-day" : "/admin" }] : []),
  ];

  return (
    <header 
      className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div className="flex items-center justify-between px-4 py-2">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/logo.png" 
            alt="APL" 
            data-testid="top-bar-logo"
            className="w-9 h-9 object-contain rounded-lg"
          />
        </Link>

        {/* Right Side */}
        <div className="flex items-center gap-2">
          {user ? (
            <>
              {/* Notifications */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    setShowProfile(false);
                  }}
                  className="relative p-2 text-slate-400 hover:text-white transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell size={22} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
                
                {showNotifications && (
                  <NotificationDropdown 
                    onClose={() => setShowNotifications(false)}
                    onUnreadUpdate={setUnreadCount}
                  />
                )}
              </div>

              {/* Profile */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => {
                    setShowProfile(!showProfile);
                    setShowNotifications(false);
                  }}
                  className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-semibold text-sm"
                  data-testid="profile-icon"
                >
                  {getInitial()}
                </button>

                {/* Profile Dropdown */}
                {showProfile && (
                  <div className="absolute right-0 top-12 w-56 bg-slate-800 border border-slate-700 rounded-xl shadow-xl overflow-hidden z-[100]" data-testid="profile-dropdown">
                    {/* User Info */}
                    <div className="p-3 border-b border-slate-700 bg-slate-900/50">
                      <p className="text-white font-medium text-sm truncate" data-testid="profile-dropdown-name">
                        {dbUser?.display_name || dbUser?.preferred_team_name || user?.displayName || "User"}
                      </p>
                      <p className="text-slate-500 text-xs truncate">
                        {user?.email}
                      </p>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      {menuItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.path}
                            onClick={() => {
                              navigate(item.path);
                              setShowProfile(false);
                            }}
                            className="w-full px-3 py-2 flex items-center gap-3 text-slate-300 hover:bg-slate-700/50 transition-colors"
                          >
                            <Icon size={16} className={item.emphasize ? "text-amber-400" : "text-slate-500"} />
                            <span className={`text-sm ${item.emphasize ? "font-bold text-amber-400" : ""}`}>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Logout */}
                    <div className="border-t border-slate-700 p-1">
                      <button
                        onClick={handleLogout}
                        className="w-full px-3 py-2 flex items-center gap-3 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        data-testid="logout-btn"
                      >
                        <LogOut size={16} />
                        <span className="text-sm">Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link
              to="/auth"
              className="px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors"
            >
              Login
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
